import { cohortApiUrl } from "../actions/api";
import { ICDNode } from '../components/create/types/ICDNode';
import {
    CohortDataset,
    Criterion,
    Group,
    Query
} from '../containers/HealthcareUsageReport/types/CohortQuery';
import { AppState } from "../types/AppState";
import { Project } from '../types/Project';
import { CONFIG } from "../util/config";
import {getDateRangeIP, getDateRangeOPCRD} from './settings';

export function randomId() {
    // Math.random should be unique because of its seeding algorithm.
    // Convert it to base 36 (numbers + letters), and grab the first 9 characters
    // after the decimal.
    return '_' + Math.random().toString(36).substr(2, 9);
}

export function randomInt() {
    return -parseInt(Math.random().toString().substr(2));
}

export const isBlank = (value: any): value is null | undefined => {
    return (isNotDefined(value) || value.trim === '' || value.length === 0)
}

export const isNotBlank = (value: any): value is object => {
    return !isBlank(value)
}

export const isDefined = (value: any): value is object => {
    return (typeof value !== 'undefined' && value !== null)
}

export const isNotDefined = (value: any): value is null | undefined => {
    return !isDefined(value)
}

export const getContentsFromStudy = ({study, queryId, groupId, criterionId}: {
    study: Project,
    queryId: number | null,
    groupId: string | null,
    criterionId: string | null}
) => {
    let query, group, criterion;

    if (isDefined(study) && isDefined(study.queries)) {
        query = study.queries[0]
        if (isNotBlank(queryId)) {
            query = study.queries.find((i: Query) => i.id === queryId)
        }

        if (isDefined(query) && isDefined(query.groups)) {
            group = query.groups[0]
            if (isNotBlank(groupId)) {
                group = query.groups.find((i: Group) => i.id === groupId)
            }

            if (isDefined(group) && isDefined(group.criteria)) {
                criterion = group.criteria[0]
                if (isNotBlank(criterionId)) {
                    criterion = group.criteria.find((i: Criterion) => i.id === criterionId)
                }
            }
        }
    }
    return { study, query, group, criterion }
}

export const getCurrent = (state: AppState) => {

    let study = state.current.study;
    let queryId = state.current.queryId;
    let groupId = state.current.groupId;
    let criterionId = state.current.criterionId;

    return getContentsFromStudy({
        study,
        queryId,
        groupId,
        criterionId
    })
}

export const findCriterion = (study: Project, queryId: number, groupId: string, criterionId: string) => {
    const { query, group } = findGroup(study, queryId, groupId)
    const criterion = group!.criteria.find((i: Criterion) => i.id.toString() === criterionId.toString())
    return {
        query,
        group,
        criterion
    }
}

export const findGroup = (study: Project, queryId: number, groupId: string) => {
    const query = findQuery(study, queryId);
    const group = query!.groups.find((i: Group) => i.id.toString() === groupId.toString())
    return {
        query,
        group
    }
}

export const findQuery = (study: Project, queryId: number): Query | undefined => {
    return study.queries.find(i => i.id.toString() === queryId.toString())
}

export const findStudy = (studies: Project[] | undefined, studyId: number): Project | undefined => {
    if (studies) {
        return studies.find(i => i.id.toString() === studyId.toString())
    }
    return undefined
}

export interface ParseDateRange {
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number
}

export const parseDateRange = (range: string): ParseDateRange => {
    const [start, end] = range.split(",")
    const [sYear, sMonth] = start.split("/")
    const [eYear, eMonth] = end.split("/")

    return {
        startYear: parseInt(sYear),
        startMonth: parseInt(sMonth),
        endYear: parseInt(eYear),
        endMonth: parseInt(eMonth),
    }
}

// TODO: cleanup by removing
export const parseDateRangeIP = (): ParseDateRange => {
    return parseDateRange(getDateRangeIP()!)
}

// TODO: cleanup by removing
export const parseDateRangeOPCRD = (): ParseDateRange => {
    return parseDateRange(getDateRangeOPCRD()!)
}

export const calculateMonths = (range: ParseDateRange) => {
    const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let monthYear = [];
    let monthYearVal = {};

    let startMonth = range.startMonth - 1;
    let endMonth = 12;
    for (let i = range.startYear; i <= range.endYear; i++) {
        if (i === range.endYear) {
            endMonth = range.endMonth
        }
        for (var j = startMonth; j < endMonth; j++) {
            monthYear.push(month[j] + ' ' + i);
            let m = (j + 1).toString()
            if (m.length === 1) {
                m = '0' + m
            }
            // @ts-ignore
            monthYearVal[month[j] + ' ' + i] = m + "/" + i;
        }
        startMonth = 0;
    }
    return { monthYear, monthYearVal }
}

// TODO: cleanup by removing
export const calculateMonthsIP = () => {
    return calculateMonths(parseDateRangeIP())
}

// TODO: cleanup by removing
export const calculateMonthsOPCRD = () => {
    return calculateMonths(parseDateRangeOPCRD())
}

export const monthsRange = (dataset: CohortDataset) => {
    const {monthYear, monthYearVal} = dataset.toString() === 'HES' ? calculateMonthsIP() : calculateMonthsOPCRD()
    // @ts-ignore
    const min = monthYearVal[monthYear[0]]
    // @ts-ignore
    const max = monthYearVal[monthYear[monthYear.length - 1]]
    return { min, max }
}

export const maxMonthsValue = 3 * 12

export const toURL = (suffix: string, onlyUseHostName = false) => {
    if (onlyUseHostName) {
        return CONFIG.hostName + suffix
    } else {
        return cohortApiUrl() + suffix
    }
}

// TODO: search by key could be fast by understanding what the key means
export const findKey = (tree: ICDNode[], key: string): ICDNode | undefined => {
    let result: ICDNode | undefined = undefined
    for (let i of tree) {
        if (i.key === key) {
            result = i
            break
        } else if (i.children) {
            result = findKey(i.children, key)
            if (result) {
                break
            }
        }
    }
    return result
}

export const findCode = (tree: ICDNode[], code: string): ICDNode | undefined => {
    let result: ICDNode | undefined = undefined
    for (let i of tree) {
        if (i.code === code) {
            result = i
            break
        } else if (i.children) {
            result = findCode(i.children, code)
            if (result) {
                break
            }
        }
    }
    return result
}

export const calculateIndexMap = (query: Query) => {
    const result: { [key: string]: boolean } = {}
    let lastWasIndex = true
    let lastOperatorWasOr = false
    query.groups.forEach(i => {
        if (!i.index) {
            if (lastWasIndex && lastOperatorWasOr) {
                result[i.id] = true
            }
        } else {
            result[i.id] = true
        }
        lastWasIndex = result[i.id]
        lastOperatorWasOr = ("or" === i.operator.join(""))
    })
    return result
}